import { PAYROLL_RUN_SORTS, PAYROLL_RUN_STATUSES, type PayrollRunStatus } from '@sahab/contracts'
import { z } from 'zod'

export const PAGE_SIZES = [25, 50, 100] as const

const pageSize = z
  .number()
  .refine((n): n is (typeof PAGE_SIZES)[number] => (PAGE_SIZES as readonly number[]).includes(n))
  .optional()
  .catch(undefined)

const page = z.number().int().min(1).optional().catch(undefined)

const q = z.coerce.string().trim().max(200).optional().catch(undefined)

type RunSortField = (typeof PAYROLL_RUN_SORTS)[number]
export type RunSort = RunSortField | `-${RunSortField}`

export function isRunSort(value: string): value is RunSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (PAYROLL_RUN_SORTS as readonly string[]).includes(field)
}

/** The payroll runs list in the URL: status, year, sort and page. */
export const runSearchSchema = z.object({
  page,
  pageSize,
  sort: z.string().refine(isRunSort).optional().catch(undefined),
  status: z.enum(PAYROLL_RUN_STATUSES).optional().catch(undefined),
  year: z.number().int().min(2000).max(2100).optional().catch(undefined),
})
export type RunSearch = z.infer<typeof runSearchSchema>

export interface RunListParams {
  page: number
  pageSize: number
  sort: string
  status: PayrollRunStatus | undefined
  year: number | undefined
}

/** Newest period first. */
export const RUN_DEFAULT_SORT = '-period'

export function toRunListQuery(search: RunSearch): RunListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? RUN_DEFAULT_SORT,
    status: search.status,
    year: search.year,
  }
}

export const RUN_TABS = ['payslips', 'inputs', 'contributions', 'alerts'] as const
export type RunTab = (typeof RUN_TABS)[number]

const PAYSLIP_SORT_FIELDS = ['number', 'name', 'gross', 'net'] as const
export type PayslipSort = (typeof PAYSLIP_SORT_FIELDS)[number] | `-${(typeof PAYSLIP_SORT_FIELDS)[number]}`

export function isPayslipSort(value: string): value is PayslipSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (PAYSLIP_SORT_FIELDS as readonly string[]).includes(field)
}

/** A run's page: the open tab, and the payslips' search, sort and page. */
export const runPageSearchSchema = z.object({
  tab: z.enum(RUN_TABS).optional().catch(undefined),
  q,
  sort: z.string().refine(isPayslipSort).optional().catch(undefined),
  page,
})
export type RunPageSearch = z.infer<typeof runPageSearchSchema>

export interface PayslipListParams {
  page: number
  pageSize: number
  q: string | undefined
  sort: string
}

export const PAYSLIP_PAGE_SIZE = 50
